import { FC, useEffect, useContext } from 'react'
import { Slider, Slide, CarouselContext } from 'pure-react-carousel'
import { useCarousel } from 'components/Common/ImagesCarousel'
import { Image } from '../_methods'
import 'pure-react-carousel/dist/react-carousel.es.css'
import { useCheckMobile } from 'utils/hooks/useMediaQuery'
import { useImageTransform } from 'components/Common/ImagesCarousel/ImageTransformProvider'
import { useRotatedImageSrc } from 'components/Common/ImagesCarousel/ImageTransformProvider/useRotatedImageSrc'
// @ts-ignore
import { InnerImageZoom } from 'react-inner-image-zoom'
import 'react-inner-image-zoom/lib/styles.min.css'
import { ImageElement, ImageWrapper, StyledCarouselProvider } from './_styles'

interface CurrentImageProps {
  images: Image[]
}

const CurrentImage: FC<CurrentImageProps> = ({ images }) => {
  const { currentImage, setTotalImages } = useCarousel()
  const isMobile = useCheckMobile()

  if (!images.length) {
    return null
  }

  useEffect(() => {
    setTotalImages(images.length)
  }, [images.length, setTotalImages])

  return (
    <StyledCarouselProvider
      naturalSlideWidth={1}
      naturalSlideHeight={1}
      totalSlides={images.length}
      step={1}
      visibleSlides={1}
      currentSlide={currentImage}
      isIntrinsicHeight={false}
      infinite={isMobile}
    >
      <CarouselSync />

      <Slider>
        {images.map((image, index) => (
          <Slide key={`${index}-${image.imageUrl}`} index={index}>
            <RotatableImage src={image.imageUrl} alt={`Image ${index + 1}`} />
          </Slide>
        ))}
      </Slider>
    </StyledCarouselProvider>
  )
}

export default CurrentImage

const RotatableImage = ({ src, alt }: { src: string; alt: string }) => {
  const {
    setImageRef,
    setZoomImageRef,
    getZoomScaleForSrc,
    scale,
    rotation,
    scaleChangeEnabled,
  } = useImageTransform()
  const zoomSrc = useRotatedImageSrc(src, rotation)
  const zoomScale = getZoomScaleForSrc(src)

  return (
    <ImageWrapper $rotation={rotation}>
      {scaleChangeEnabled ? (
        <InnerImageZoom
          key={`${scale}-${rotation}-${src}`}
          // @ts-ignore
          ref={setZoomImageRef}
          src={zoomSrc}
          alt={alt}
          zoomType='hover'
          zoomScale={zoomScale}
        />
      ) : (
        <ImageElement
          ref={setImageRef}
          src={src}
          alt={alt}
          className='carousel__image'
          draggable={false}
          onMouseDown={e => {
            e.currentTarget.style.cursor = 'grabbing'
          }}
          onMouseUp={e => {
            e.currentTarget.style.cursor = 'grab'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.cursor = 'grab'
          }}
        />
      )}
    </ImageWrapper>
  )
}

RotatableImage.displayName = 'RotatableImage'

const CarouselSync: FC = () => {
  const { currentImage, setCurrentImage } = useCarousel()
  const carouselContext = useContext(CarouselContext)
  const { resetRotation } = useImageTransform()

  useEffect(() => {
    const handleSlideChange = () => {
      const newCurrentSlide = carouselContext.state.currentSlide
      if (newCurrentSlide !== currentImage) {
        setCurrentImage(newCurrentSlide)
        resetRotation()
      }
    }

    carouselContext.subscribe(handleSlideChange)
    return () => carouselContext.unsubscribe(handleSlideChange)
  }, [carouselContext, currentImage, setCurrentImage, resetRotation])

  useEffect(() => {
    if (carouselContext.state.currentSlide !== currentImage) {
      carouselContext.setStoreState({ currentSlide: currentImage })
    }
  }, [currentImage, carouselContext])

  return null
}
